import axios, { AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Log500Error, LogTimeoutError, LogApiTime } from '@/service/monitor';
import { terminal, UserAgentInfo } from '@/constant/index';
import cookie, { getTopLevelDomain } from '@/utils/cookie';
import { shopify, shopline } from '@/constant/company';
import { isDisableFormEvent } from '@/utils/experiment';
import { getEnv, hexToRgb } from '@/utils';
import { sanitizeStyleContent } from '@/utils/safeHtml';
import { getMiddlePageValues, getMiddleScopedKey } from '@/utils/formPages';
import { generateSign } from '@/utils/sign';
import { debug } from 'debug';
import { formEventV2 } from '@/service/formEventV2';
import { setCookie } from '@/service/strategy/cookieLogic';
import { STORAGE_KEY } from '@/constant/constant';
import { getLocal, getSession, setLocal } from '@/utils/storage';
import { getDeployEnv, isFormEventDeployEnv, isTestDeployEnv } from '@/utils/platform';
import { getShopifyCustomerLocale } from '@/utils/shopifyLocale';
import { safeJsonParse } from 'utils/safeJsonParse';
import { withFormResponseOptimizeVer } from '@/utils/formResponseOptimize';

const log = debug('cartsee:api');

// 用于 form/out/signup 签名的 AppKey，以 base64 存储，使用时用 atob 解码（浏览器标准 API）
const APP_KEY_B64 = 'eUxhSUpTQ3NOOEZKbFB6TkNEYmphbEhTWHBwVllvMEY=';

/** Base64 解码，使用浏览器原生 atob（Web API，所有现代浏览器支持） */
function decodeAppKey(): string {
  return atob(APP_KEY_B64);
}
// 添加请求拦截器
let httpStartTime;
let httpEndTime;
axios.interceptors.request.use(
  function (config) {
    // 在发送请求之前做些什么
    httpStartTime = new Date().getTime();
    return config;
  },
  function (error) {
    // 对请求错误做些什么
    return Promise.reject(error);
  }
);
// @ts-ignore
axios.defaults.retry = 2; // 重试3次
// @ts-ignore
axios.defaults.retryDelay = 1000; // 间隔1秒

axios.interceptors.response.use(
  function (response) {
    // 响应状态码为 2xx 时触发成功的回调，形参中的 response 是“成功的结果”
    httpEndTime = new Date().getTime();
    let apiTime = httpEndTime - httpStartTime;
    let apiArray = ['form/out/subscribeLoad', 'form/out/view', 'form/out/signup', 'form/out/success'];
    apiArray.forEach((api) => {
      if (response.config.url?.indexOf(api) != -1) {
        LogApiTime({
          ru: response.config?.url || response.request?.custom?.url || '', // request url
          rp: response.config?.data || response.request?.custom?.options?.body || '', // request params
          ru1: response.request?.responseURL || '', // response url
          m: response.config?.method || 'POST', // method
          time: apiTime
        });
      }
    });
    return response;
  },
  function (error) {
    if (getcartAnimate() === 'lf0hi4rj' || getcartAnimate() === 'l2y7m6c5') {
      //328和30测试仿真的日志记录
      if (error?.config?.url === 'https://cartsee-form-c.cartx.cloud/cartsee/form/out/subscribeLoad') {
        setLocal(STORAGE_KEY.LOAD_ERROR, `${error?.message}-retry-${error?.config?.__retry}}`);
      }

      if (error?.config?.url === 'https://cartsee-form-c.cartx.cloud/cartsee/form/out/view') {
        setLocal(STORAGE_KEY.VIEW_ERROR, `${error?.message}-retry-${error?.config?.__retry}}`);
      }
    }

    if (error.request && error.request.readyState == 4 && error.request.status >= 500) {
      // 500 以上的服务器请求，触发埋点上报:
      Log500Error({
        ru: error.config?.url || error.request?.custom?.url || '', // request url
        rp: error.config?.data || error.request?.custom?.options?.body || '', // request params
        ru1: error.request?.responseURL || '', // response url
        m: error.config?.method || 'POST', // method
        em: error.message || error.status || '500+', // error message
        er: error.response?.data || '' // error response
      });
    }

    if (error.message.includes('timeout')) {
      // 报个超时状态出去
      LogTimeoutError({
        ru: error.config?.url || error.request?.custom?.url || '', // request url
        rp: error.config?.data || error.request?.custom?.options?.body || '', // request params
        ru1: error.request?.responseURL || '', // response url
        m: error.config?.method || 'POST', // method
        em: error.message || error.status || error.request?.status || '504', // error message
        er: error.response?.data || '' // error response
      });
    }

    // 重试，checkFormSettings检查配置不进行重试，请求失败直接拿本地的
    if (error.request && error.request.readyState == 4 && !error.config?.url?.includes('/checkFormSettings')) {
      // 获取错误中的请求配置
      const config = error.config;
      if (!config || !config.retry) {
        // 没有配置，或没有设置重试次数
        return Promise.reject(error);
      }
      // 设置重试次数，用于跟踪
      config.__retry = config.__retry || 0;
      // 检查是否超出重试次数
      if (config.__retry >= config.retry) {
        return Promise.reject(error);
      }
      /**                重试逻辑                */
      config.__retry += 1; // 进入到重试逻辑，次数+1
      // 重试延时
      const retryAxios = new Promise((resolve) => {
        // 1秒后重试，或使用配置
        setTimeout(() => {
          resolve('');
        }, config.retryDelay || 1000);
      });

      // 返回promise链
      return retryAxios.then(() => {
        // console.log('retry', config);
        return axios(config);
      });
    }

    return Promise.reject(error);
  }
);

export const getcartAnimateByEntryName = (entryName) => {
  let str = '';
  let cartAnimate = document.getElementsByTagName('script');
  for (let i = 0; i < cartAnimate.length; i++) {
    if (cartAnimate[i].src?.indexOf(entryName + '.js') > 0) {
      str = cartAnimate[i].src;
    }
    if (cartAnimate[i]?.getAttribute('data-src')?.indexOf(entryName + '.js') > 0) {
      str = cartAnimate[i].getAttribute('data-src');
    }
  }
  let id = str.match(new RegExp('id=([^&]+)')) || [];
  // return id[1] || 'l2y7m6c5';
  return id[1] || (isLocalDevHost() ?  'l2y7m6c5' : '');
};

export const COOKIE_UUID = '_cartsee_uid';

const isLocalDevHost = () => {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
  );
};

// 修改该对象时 config-overrides.js 需要同修改
const deployEnvMap = {
  shopify: {
    entryName: 'cartAnimate',
    chunkFilePath: 'static',
    getShopifyUserId: () => {
      const shopifyId = getCookie('_shopify_y');
      // 自建uuid
      if (!shopifyId) {
        const companyUUID = getcartAnimateByEntryName('cartAnimate');
        const cartseeUUID = getLocal(companyUUID);
        if (!cartseeUUID) {
          const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
          setLocal(companyUUID, uuid);
          setLocal(COOKIE_UUID, uuid);
        }
      }
      return getLocal(COOKIE_UUID) || getCookie('_shopify_y') || '';
    },
    apiBaseUrl: 'https://cartsee.cartx.cloud'
  },
  shopifyTest: {
    entryName: 'cartAnimateTest',
    chunkFilePath: 'static-test',
    getShopifyUserId: () => {
      const shopifyId = getCookie('_shopify_y');
      // 自建uuid
      if (!shopifyId) {
        const companyUUID = getcartAnimateByEntryName('cartAnimateTest');
        const cartseeUUID = getLocal(companyUUID);
        if (!cartseeUUID) {
          const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
          setLocal(companyUUID, uuid);
          setLocal(COOKIE_UUID, uuid);
        }
      }
      return getLocal(COOKIE_UUID) || getCookie('_shopify_y') || '';
    },
    apiBaseUrl: 'https://test-cartsee-c.cartx.cloud'
  },
  shopline: {
    entryName: 'cart-animate-shopline',
    chunkFilePath: 'static-shopline',
    getShopifyUserId: () => {
      const companyUUID = getcartAnimateByEntryName('cart-animate-shopline');
      let cartseeUUID = cookie.getCookie(companyUUID) || cookie.getCookie('cartsee_uid');
      if (!cartseeUUID || cartseeUUID === 'undefined') {
        cartseeUUID = JSON.parse(getCookie('n_sess') || '{}').session_id;
        cookie.setCookie('cartsee_uid', cartseeUUID, 365, '/');
      }

      // 以第一次到站用户进行companyUUID维度归一
      if (!cookie.getCookie(companyUUID) && cartseeUUID) {
        cookie.setCookie(companyUUID, cartseeUUID, 365, '/', getTopLevelDomain());
      }
      // 未同步
      if (cookie.getCookie(companyUUID) !== cookie.getCookie(COOKIE_UUID)) {
        // 暴露给cartxtrack的uid，保证companyUUID先失效，同步更新COOKIE_UUID，保存到当前域名
        cookie.setCookie(COOKIE_UUID, cartseeUUID, 366, '/');
      }

      // 自建uuid
      if (!cartseeUUID) {
        const lsCartseeUUID = getLocal(companyUUID);
        if (!lsCartseeUUID) {
          const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
          setLocal(companyUUID, uuid);
          setLocal(COOKIE_UUID, uuid);
        }
      }

      return getLocal(COOKIE_UUID) || cartseeUUID || JSON.parse(getCookie('n_sess') || '{}').session_id;
    },
    apiBaseUrl: 'https://cartsee.cartx.cloud'
  },
  shoplineTest: {
    entryName: 'cart-animate-shopline-test',
    chunkFilePath: 'static-shopline-test',
    getShopifyUserId: () => {
      const companyUUID = getcartAnimateByEntryName('cart-animate-shopline-test');
      let cartseeUUID = cookie.getCookie(companyUUID) || cookie.getCookie('cartsee_uid');
      if (!cartseeUUID || cartseeUUID === 'undefined') {
        cartseeUUID = JSON.parse(getCookie('n_sess') || '{}').session_id;
        cookie.setCookie('cartsee_uid', cartseeUUID, 365, '/');
      }

      // 以第一次到站用户进行companyUUID维度归一
      if (!cookie.getCookie(companyUUID) && cartseeUUID) {
        cookie.setCookie(companyUUID, cartseeUUID, 365, '/', getTopLevelDomain());
      }
      // 未同步
      if (cookie.getCookie(companyUUID) !== cookie.getCookie(COOKIE_UUID)) {
        // 暴露给cartxtrack的uid，保证companyUUID先失效，同步更新COOKIE_UUID，保存到当前域名
        cookie.setCookie(COOKIE_UUID, cartseeUUID, 366, '/');
      }

      // 自建uuid
      if (!cartseeUUID) {
        const lsCartseeUUID = getLocal(companyUUID);
        if (!lsCartseeUUID) {
          const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
          setLocal(companyUUID, uuid);
          setLocal(COOKIE_UUID, uuid);
        }
      }

      return getLocal(COOKIE_UUID) || cartseeUUID || JSON.parse(getCookie('n_sess') || '{}').session_id;
    },
    apiBaseUrl: 'https://test-cartsee-c.cartx.cloud'
  },
  shoplazza: {
    entryName: 'cart-animate-shoplazza',
    chunkFilePath: 'static-shoplazza',
    getShopifyUserId: () => {
      const userId = getCookie('_c_id');
      // 自建uuid
      if (!userId) {
        const companyUUID = getcartAnimateByEntryName('cart-animate-shoplazza');
        const cartseeUUID = getLocal(companyUUID);
        if (!cartseeUUID) {
          const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
          setLocal(companyUUID, uuid);
          setLocal(COOKIE_UUID, uuid);
        }
      }
      return getLocal(COOKIE_UUID) || getCookie('_c_id') || '';
    },
    apiBaseUrl: 'https://cartsee.cartx.cloud'
  },
  shoplazzaTest: {
    entryName: 'cart-animate-shoplazza-test',
    chunkFilePath: 'static-shoplazza-test',
    getShopifyUserId: () => {
      const userId = getCookie('_c_id');
      // 自建uuid
      if (!userId) {
        const companyUUID = getcartAnimateByEntryName('cart-animate-shoplazza-test');
        const cartseeUUID = getLocal(companyUUID);
        if (!cartseeUUID) {
          const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
          setLocal(companyUUID, uuid);
          setLocal(COOKIE_UUID, uuid);
        }
      }
      return getLocal(COOKIE_UUID) || getCookie('_c_id') || '';
    },
    apiBaseUrl: 'https://test-cartsee-c.cartx.cloud'
  },
  // todo: xike的user ID需要给出 也就是埋点处的uid
  xike: {
    entryName: 'cart-animate-xike',
    chunkFilePath: 'static-xike',
    getShopifyUserId: () => {
      return getCookie('mirage_cid') || '';
    },
    apiBaseUrl: 'https://cartsee.cartx.cloud'
  },
  xikeTest: {
    entryName: 'cart-animate-xike-test',
    chunkFilePath: 'static-xike-test',
    getShopifyUserId: () => {
      return getCookie('mirage_cid') || '';
    },
    apiBaseUrl: 'https://test-cartsee-c.cartx.cloud'
  },
  common: {
    entryName: 'cart-animate-common',
    chunkFilePath: 'static-common',
    getShopifyUserId: () => {
      return getCookie('cartx-cookie') || '';
    },
    apiBaseUrl: 'https://cartsee.cartx.cloud'
  },
  commonTest: {
    entryName: 'cart-animate-common-test',
    chunkFilePath: 'static-common-test',
    getShopifyUserId: () => {
      return getCookie('cartx-cookie') || '';
    },
    apiBaseUrl: 'https://test-cartsee-c.cartx.cloud'
  },
  simple: {
    entryName: 'cart-animate-simple',
    chunkFilePath: 'static-simple',
    getShopifyUserId: () => {
      const cookie = getCookie('cartx-cookie');
      if (cookie) {
        return cookie;
      }

      const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
      setCookie('cartx-cookie', uuid);
      return uuid;
    },
    apiBaseUrl: 'https://cartsee.cartx.cloud'
  },
  simpleTest: {
    entryName: 'cart-animate-simple-test',
    chunkFilePath: 'static-simple-test',
    getShopifyUserId: () => {
      const cookie = getCookie('cartx-cookie');
      if (cookie) {
        return cookie;
      }

      const uuid = `${companyUUID}-${uuidv4()}-${new Date().getTime()}`;
      setCookie('cartx-cookie', uuid);
      return uuid;
    },
    apiBaseUrl: 'https://test-cartsee-c.cartx.cloud'
  },
  woocommerce: {
    entryName: 'cart-animate-woocommerce',
    chunkFilePath: 'static-woocommerce',
    getShopifyUserId: () => {
      return getCookie('cartsee_uid') || '';
    },
    apiBaseUrl: 'https://cartsee.cartx.cloud'
  },
  woocommerceTest: {
    entryName: 'cart-animate-woocommerce-test',
    chunkFilePath: 'static-woocommerce-test',
    getShopifyUserId: () => {
      return getCookie('cartsee_uid') || '';
    },
    apiBaseUrl: 'https://test-cartsee-c.cartx.cloud'
  },
  shopyy: {
    entryName: 'cart-animate-shopyy',
    chunkFilePath: 'static-shopyy',
    getShopifyUserId: function () {
      return window?.oemcart_global_unique_id || '';
    },
    apiBaseUrl: 'https://cartsee.cartx.cloud'
  },
  shopyyTest: {
    entryName: 'cart-animate-shopyy-test',
    chunkFilePath: 'static-shopyy-test',
    getShopifyUserId: function () {
      return window?.oemcart_global_unique_id || '';
    },
    apiBaseUrl: 'https://test-cartsee-c.cartx.cloud'
  }
};
const deployInfo = deployEnvMap[getDeployEnv()] || deployEnvMap.shopify;

//获取公司ID
export const getcartAnimate = (): string => {
  let str = '';
  let cartAnimate = document.getElementsByTagName('script');
  for (let i = 0; i < cartAnimate.length; i++) {
    if (cartAnimate[i].src?.indexOf(deployInfo.entryName + '.js') > 0) {
      str = cartAnimate[i].src;
    }
    if (cartAnimate[i]?.getAttribute('data-src')?.indexOf(deployInfo.entryName + '.js') > 0) {
      str = cartAnimate[i].getAttribute('data-src');
    }
  }
  let id = str.match(new RegExp('id=([^&]+)')) || [];
  // return id[1] || 'l2y7m6c5';
  return id[1] || (isLocalDevHost() ?  'l2y7m6c5' : '');
};

export const companyUUID = getcartAnimate();

//接口通用方法
export async function commonApi(params: any, action: string): Promise<AxiosResponse> {
  let origin = '';
  params = withFormResponseOptimizeVer(params, action, companyUUID);
  if (process.env.NODE_ENV === 'production') {
    if (['l2y7m6c5', 'lef9edad', 'lnjzugty', 'leca9wq8', 'lt2n54ww'].includes(companyUUID)) {
      origin = 'https://test-cartsee-c.cartx.cloud';
    } else {
      origin = 'https://cartsee-form-c.cartx.cloud';
    }
  } else {
    origin = 'https://test-cartsee-c.cartx.cloud';
  }
  // 只有公司14 和 328 ac站走formEventV2的埋点灰度(还有30是为了本地测试)
  // 'lef9edad' 313 开埋点
  if (action === 'form/formEventV2') {
    // 317加stream对比埋点lembmevv，306加stream对比埋点leca9wq8, 671lpgoyvck

    if ((window as any).__CARTSEE_FORM_EVENTV2__ && typeof (window as any).__CARTSEE_FORM_EVENTV2__ === 'function') {
      return (window as any).__CARTSEE_FORM_EVENTV2__(params);
    }

    // 1812
    if (companyUUID === 'm5cahhz3' || companyUUID === 'mdps1ko6' || companyUUID === 'lwq5wwol' || isDisableFormEvent(params)) {
      return Promise.resolve({} as AxiosResponse);
    } else if (
      companyUUID === 'l17m1y40' ||
      companyUUID === 'lef9edad' ||
      companyUUID === 'll67j061' ||
      companyUUID === 'lb08lu3r' ||
      companyUUID === 'lembmevv' ||
      companyUUID === 'lpgoyvck' ||
      companyUUID === 'leca9wq8' ||
      companyUUID === 'l2y7m6c5' ||
      companyUUID === 'lf0hi4rj' ||
      companyUUID === 'lemkl2iy' ||
      companyUUID === 'lijrpzzr' ||
      companyUUID === 'lezd196i' ||
      companyUUID === 'lgotkqvw' ||
      companyUUID === 'lgoth4v0' ||
      companyUUID === 'lyxvjtdb' ||
      isFormEventDeployEnv()
    ) {
      return axios.post(`${origin}/cartsee/${action}`, params, {
        // @ts-ignore
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        timeout: 20 * 1000
      });
    } else {
      return Promise.resolve({} as AxiosResponse);
    }
  } else {
    if (process.env.NODE_ENV === 'production') {
      if (isTestDeployEnv()) {
        if ([shopify[2317], shopline[838]].includes(companyUUID)) {
          if (['form/out/signup'].includes(action)) {
            origin = 'https://a1.clink-lists.com';
          } else if (['form/out/view'].includes(action)) {
            origin = 'https://a2.clink-lists.com';
          } else {
            origin = 'https://test-static-forms.clink-lists.com';
          }
        } else {
          origin = 'https://test-static-forms.clink-lists.com';
        }
      } else {
        if ([shopify[2317], shopline[838]].includes(companyUUID)) {
          if (['form/out/signup'].includes(action)) {
            origin = 'https://a1.clink-lists.com';
          } else if (['form/out/view'].includes(action)) {
            origin = 'https://a2.clink-lists.com';
          } else {
            origin = 'https://static-forms.clink-lists.com';
          }
        } else {
          origin = 'https://static-forms.clink-lists.com';
        }
      }
    } else {
      if ([shopify[2317], shopline[838]].includes(companyUUID)) {
        if (['form/out/signup'].includes(action)) {
          origin = 'https://a1.clink-lists.com';
        } else if (['form/out/view'].includes(action)) {
          origin = 'https://a2.clink-lists.com';
        } else {
          origin = 'https://static-forms.clink-lists.com';
        }
      } else {
        origin = 'https://test-static-forms.clink-lists.com';
      }
    }

    try {
      params.env = getEnv();
      params.browserBrand = UserAgentInfo().browserBrand;
    } catch (e) {
      console.error(e);
    }

    // form/out/signup 提交：不区分渠道，window.Shopify.locale/country 齐全时上报 customerLocale（如 en-FR）
    if (action === 'form/out/signup') {
      const customerLocale = getShopifyCustomerLocale();
      if (customerLocale) {
        params.customerLocale = customerLocale;
      }
    }

    // signup/success/subCode 接口需要添加签名到 headers（AppKey 为 base64 存储，使用时解码）
    let headers: Record<string, string> | undefined;
    if (action === 'form/out/signup' || action === 'form/out/success' || action === 'form/out/subCode') {
      try {
        const appKey = decodeAppKey();
        if (appKey) {
          const requestBody = JSON.stringify(params);
          const sign = await generateSign(requestBody, appKey);
          headers = { sign };
          log('Generated sign for %s', action);
        }
      } catch (error) {
        log('Failed to generate sign:', error);
      }
    }

    const response = await axios.post(`${origin}/cartsee/${action}`, params, {
      // @ts-ignore
      'Access-Control-Allow-Origin': window?.location?.host || '*',
      'Content-Type': 'application/json',
      ...(headers && { headers }), // 只在有 sign 时添加 headers
      timeout: 20 * 1000
    });
    
    return response;
  }
}

export function dynamicApi(params, action) {
  let origin = '';

  if (process.env.NODE_ENV === 'production') {
    // 测试站点
    if (['l2y7m6c5', 'lef9edad', 'lnjzugty', 'leca9wq8'].includes(companyUUID)) {
      origin = 'https://test-api.cartsee.cartx.cloud';
    } else {
      origin = 'https://api.cartsee.cartx.cloud';
    }
  } else {
    origin = 'https://test-api.cartsee.cartx.cloud';
  }

  return axios.post(`${origin}/cartsee/${action}`, params, {
    // @ts-ignore
    'Access-Control-Allow-Origin': window?.location?.host || '*',
    'Content-Type': 'application/json',
    timeout: 20 * 1000
  });
}

//请求shopify cart.js API方法
export function shopifyApi(action) {
  return axios.get(`${window.location.origin}/${action}`);
}

/** Cookie 值安全解码：优先 decodeURIComponent，失败时回退 unescape（兼容 %uXXXX 与裸 %） */
function safeDecodeCookieValue(str: string): string {
  try {
    return decodeURIComponent(str);
  } catch {
    return unescape(str); // 兼容旧 cookie（%uXXXX）或含未编码 % 的值
  }
}

//获取Cookie
export function getCookie(c_name) {
  if (document?.cookie?.length > 0) {
    var c_start = document.cookie.indexOf(c_name + '=');
    if (c_start !== -1) {
      c_start = c_start + c_name.length + 1;
      var c_end = document.cookie.indexOf(';', c_start);
      if (c_end === -1) c_end = document.cookie.length;
      return safeDecodeCookieValue(document.cookie.substring(c_start, c_end));
    }
  }
  return '';
}

export const getShopifyUserId = deployInfo.getShopifyUserId;

export const getQueryVariable = (variable) => {
  // 通过 ? 分割获取后面的参数字符串
  let urlSearch = window.location.href.split('?')[1];
  if (urlSearch) {
    // 创建空对象存储参数
    let obj = {};
    // 再通过 & 将每一个参数单独分割出来
    let paramsArr = urlSearch.split('&');
    for (let i = 0, len = paramsArr.length; i < len; i++) {
      // 再通过 = 将每一个参数分割为 key:value 的形式
      let arr = paramsArr[i].split('=');
      obj[arr[0]] = arr[1];
    }
    return obj[variable];
  }
};

export const getQueryParameters = (url) => {
  const searchParams = new URLSearchParams(url?.split('?')[1]);
  const queryParams: Record<string, string> = {};

  // @ts-ignore
  for (const [key, value] of searchParams.entries()) {
    queryParams[key] = value;
  }

  return queryParams;
};

//注入自定义的style
export const customStyles = (response, id) => {
  if (document.body.offsetWidth < 800) {
    let new_maet = document.createElement('meta');
    new_maet.name = 'viewport';
    new_maet.content = 'width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no';
    document?.head?.appendChild(new_maet);
  }

  if (response.data.data && response.data.data.settings && response.data.data.settings.styles && response.data.data.settings.styles.styleMap) {
    // 兼容错误数据并修改cartx-b-button-end => cartx-b-button-end
    for (let key of Object.keys(response.data.data.settings.styles?.styleMap || {})) {
      const matchRes = key.match(/^[a-zA-Z\-.]*\.(cartx-b-[a-zA-Z\-]*-end)$/);
      if (matchRes) {
        response.data.data.settings.styles.styleMap[matchRes[1]] = response.data.data.settings.styles.styleMap[key];
        delete response.data.data.settings.styles.styleMap[key];
      }
    }
    const popWindowWheel = response.data.data?.popWindowWheel;
    const isPopWindowWheel3 = popWindowWheel === 3 || popWindowWheel === '3';
    const isFullscreenNoneWheel3 =
      isPopWindowWheel3 &&
      response.data.data?.settings?.rules?.popupLayoutMode === 'fullscreen' &&
      response.data.data?.settings?.typesetting?.imgPosition === 'none';
    const wheel3ShowStyleKeys = ['cartx-b-show', 'cartx-b-show__middle', 'cartx-b-show__end'];

    const stylesElement = Object.keys(response.data.data.settings.styles.styleMap).reduce((res, key, index) => {
      const obj = response.data.data.settings.styles.styleMap[key];
      return (
        res +
        `${id} .${key} {
          ${Object.keys(obj).reduce((res2, key2, index2) => {
            const value = obj[key2];
            if (
              isFullscreenNoneWheel3 &&
              wheel3ShowStyleKeys.includes(key) &&
              key2 === 'background'
            ) {
              return res2;
            }
            if (value) {
              return res2 + `${key2}: ${value} !important;`;
            } else {
              return res2;
            }
          }, '')}
        }`
      );
    }, '');
    const nod = document.createElement('style');
    nod.id = `${id.slice(1)}-1`;
    nod.innerHTML = sanitizeStyleContent(stylesElement);
    document.body.append(nod);

    const phoneStylesElement = `
      @media screen and (max-width: 800px) {
        ${Object.keys(response.data.data.settings.styles.styleMap).reduce((res, key, index) => {
          const obj = response.data.data.settings.styles.styleMap[key];
          if ([
            'cartx-b-title',
            'cartx-b-title__middle',
            'cartx-b-title-end',
            'cartx-b-subtitle',
            'cartx-b-subtitle__middle',
            'cartx-b-subtitle-end',
            'cartx-b-activity',
            'cartx-b-activity__middle',
            'cartx-b-activity-end',
            'cartx-b-coupon-prompt'
          ].includes(key) && Object.keys(obj).includes('font-size')) {
            const fontSize = parseFloat(obj['font-size']);
            return (
              res +
              `${id} .${key} {
                  font-size: ${(fontSize / 500) * 100}vw!important;
                }`
            );
          } else {
            return res;
          }
        }, '')}}
    `;
    const nod2 = document.createElement('style');
    nod2.id = `${id.slice(1)}-2`;
    nod2.innerHTML = sanitizeStyleContent(phoneStylesElement);
    document.body.append(nod2);
  }

  const rules = response.data.data?.settings?.rules || {};
  const timing = response.data.data?.settings?.timing || {};
  if (Object.keys(timing).length) {
    rules.optionColumns = timing.optionColumns;
    rules.optionColor = timing.optionColor;
    rules.optionBackground = timing.optionBackground;
  }
  const imgPosition = response.data.data?.settings?.typesetting?.imgPosition;
  const triggerEventType = response.data.data?.triggerEventType;
  const type = response.data.data?.type;
  const isFullscreenPopup = response.data.data?.settings?.rules?.popupLayoutMode === 'fullscreen';
  const styleMap = response.data.data?.settings?.styles?.styleMap || {};
  const allSettings = response.data.data?.settings || {};
  const middlePageValues = getMiddlePageValues(allSettings);

  const getScopedRule = (pageValue: string, baseMiddleKey: string) => {
    const scopedKey = getMiddleScopedKey(pageValue as any, baseMiddleKey);
    return (rules as any)[scopedKey] ?? (rules as any)[baseMiddleKey];
  };

  const enableOptimizeEmailHeightStyle = type !== 'WheelOfFortune';
  // 后续进行扩展
  let enableGlobalFontFamily = [shopify[920]].includes(companyUUID);
  let globalFontFamily = '"Tenor Sans", Arial !important';
  log('920 enableGlobalFontFamily=%s, globalFontFamily=%s', enableGlobalFontFamily, globalFontFamily);
  if ([shopify[2545]].includes(companyUUID)) {
    enableGlobalFontFamily = true;
    globalFontFamily = '"Produkt-Regular-Cy-Web", Arial !important';
    log('2545 enableGlobalFontFamily=%s, globalFontFamily=%s', enableGlobalFontFamily, globalFontFamily);
  }

  const enable2247StyleFix = [shopify[2247]].includes(companyUUID);

  const textAlignMap: Record<string, string> = {
    'left': 'flex-start',
    'center': 'center',
    'right': 'flex-end',
  };
  const getOptionColumnCount = (columns?: number) => Math.max(1, Math.min(Number(columns) || 1, 3));
  const getOptionFlexBasis = (columns?: number) => {
    const columnCount = getOptionColumnCount(columns);
    return columnCount === 1 ? '100%' : 'auto';
  };

  const getMiddlePageStyle = (pageValue: string) => {
    const containerSelector = `${id} .cartx-b-wheel-container__${pageValue}`;
    const privacyStyleKey = pageValue === 'middle' ? 'cartx-b-privacy__middle' : `cartx-b-privacy__${pageValue}`;
    const privacyCheckStyleKey = pageValue === 'middle' ? 'cartx-b-privacy-check__middle' : `cartx-b-privacy-check__${pageValue}`;

    const r = {
      inputType: getScopedRule(pageValue, 'middleInputType'),
      inputBorderStyle: getScopedRule(pageValue, 'middleInputBorderStyle'),
      inputBorderColor: getScopedRule(pageValue, 'middleInputBorderColor'),
      inputBorderRadius: getScopedRule(pageValue, 'middleInputBorderRadius'),
      inputBackgroundShow: getScopedRule(pageValue, 'middleInputBackgroundShow'),
      inputBackground: getScopedRule(pageValue, 'middleInputBackground'),
      inputColor: getScopedRule(pageValue, 'middleInputColor'),
      inputMarginTop: getScopedRule(pageValue, 'middleInputMarginTop'),
      inputMarginBottom: getScopedRule(pageValue, 'middleInputMarginBottom'),
      inputMarginLeft: getScopedRule(pageValue, 'middleInputMarginLeft'),
      inputMarginRight: getScopedRule(pageValue, 'middleInputMarginRight'),
      dropdownBorderStyle: getScopedRule(pageValue, 'middleDropdownBorderStyle'),
      dropdownBorderColor: getScopedRule(pageValue, 'middleDropdownBorderColor'),
      dropdownBorderRadius: getScopedRule(pageValue, 'middleDropdownBorderRadius'),
      dropdownBackgroundType: getScopedRule(pageValue, 'middleDropdownBackgroundType'),
      dropdownBackgroundColor: getScopedRule(pageValue, 'middleDropdownBackgroundColor'),
      dropdownOptionTextColor: getScopedRule(pageValue, 'middleDropdownOptionTextColor'),
      dropdownOptionTextFontFamily: getScopedRule(pageValue, 'middleDropdownOptionTextFontFamily'),
      dropdownPlaceholderColor: getScopedRule(pageValue, 'middleDropdownPlaceholderColor'),
      dropdownPlaceholderFontSize: getScopedRule(pageValue, 'middleDropdownPlaceholderFontSize'),
      dropdownTitleColor: getScopedRule(pageValue, 'middleDropdownTitleColor'),
      dropdownTitleFontSize: getScopedRule(pageValue, 'middleDropdownTitleFontSize'),
      dropdownTitleFontFamily: getScopedRule(pageValue, 'middleDropdownTitleFontFamily'),
      dropdownMarginTop: getScopedRule(pageValue, 'middleDropdownMarginTop'),
      dropdownMarginBottom: getScopedRule(pageValue, 'middleDropdownMarginBottom'),
      dropdownMarginLeft: getScopedRule(pageValue, 'middleDropdownMarginLeft'),
      dropdownMarginRight: getScopedRule(pageValue, 'middleDropdownMarginRight'),
      optionColumns: getScopedRule(pageValue, 'middleOptionColumns'),
      optionTextColor: getScopedRule(pageValue, 'middleOptionTextColor'),
      optionColor: getScopedRule(pageValue, 'middleOptionColor'),
      optionFontFamily: getScopedRule(pageValue, 'middleOptionFontFamily'),
      optionFontSize: getScopedRule(pageValue, 'middleOptionFontSize'),
      optionTitleColor: getScopedRule(pageValue, 'middleOptionTitleColor'),
      optionTitleFontSize: getScopedRule(pageValue, 'middleOptionTitleFontSize'),
      optionBackground: getScopedRule(pageValue, 'middleOptionBackground'),
      optionMarginTop: getScopedRule(pageValue, 'middleOptionMarginTop'),
      optionMarginBottom: getScopedRule(pageValue, 'middleOptionMarginBottom'),
      optionMarginLeft: getScopedRule(pageValue, 'middleOptionMarginLeft'),
      optionMarginRight: getScopedRule(pageValue, 'middleOptionMarginRight'),
    };

    let style = '';

    style += `
      /* ${pageValue} 状态的 input 样式（覆盖原有样式） */
      ${containerSelector} .cartx-form .cartx-input .arco-input,
      ${containerSelector} .cartx-form .cartx-input,
      ${containerSelector} .cartx-form .arco-picker,
      ${containerSelector} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view{
        border-width: ${r.inputType === 'bottom' ? '0 0 1px 0' : '1px'}!important;
        border-style: ${r.inputBorderStyle || 'solid'}!important;
        border-color: ${r.inputBorderColor || '#C5C4C3'}!important;
        border-radius: ${r.inputBorderRadius || '4px'}!important;
        background: ${r.inputBackgroundShow !== 'none' ? r.inputBackground || '#FFFFFF' : 'none'}!important;
        color: ${r.inputColor || '#1d2129'}!important;
        ${companyUUID === 'm7bbi3hs' ? `-webkit-text-fill-color: ${r.inputColor || '#1d2129'} !important;` : ''}
      }
      /* Arco 默认 focus 时给 picker 内部 input 套 var(--color-primary-light-1) 背景，看起来像浅色"白框"，强制透明让外层 picker 背景生效 */
      ${containerSelector} .cartx-form .arco-picker input,
      ${containerSelector} .cartx-form .arco-picker-focused .arco-picker-input-active input,
      ${containerSelector} .cartx-form .arco-picker-focused:hover .arco-picker-input-active input {
        background: transparent !important;
      }
      ${containerSelector} .cartx-form[data-form-type="input"] {
        ${r.inputMarginTop !== undefined ? `margin-top: ${r.inputMarginTop}px !important;` : ''}
        ${r.inputMarginBottom !== undefined ? `margin-bottom: ${r.inputMarginBottom}px !important;` : ''}
        ${r.inputMarginLeft !== undefined ? `margin-left: ${r.inputMarginLeft}px !important;` : ''}
        ${r.inputMarginRight !== undefined ? `margin-right: ${r.inputMarginRight}px !important;` : ''}
        width: calc(100% - ${r.inputMarginLeft || 0}px - ${r.inputMarginRight || 0}px) !important;
      /* Middle 状态的 email-suffix-list 样式（覆盖原有样式） */
      ${id} .cartx-b-wheel-container__middle .email-suffix-list{
        border-width: ${rules.middleInputType  === 'bottom' ? '0 0 1px 0' : '1px'}!important;
        border-style: ${rules.middleInputBorderStyle || 'solid'}!important;
        border-color: ${rules.middleInputBorderColor || '#0D0C16'}!important;
        border-radius: ${(rules.middleInputBorderRadius === '1000px' ? '16px' : rules.middleInputBorderRadius) || '4px'};
      }
      ${id} .cartx-b-wheel-container__middle .email-suffix-list .email-suffix-item {
        color: ${rules.middleInputColor || '#1d2129'};
      }
      ${r.inputMarginLeft !== undefined || r.inputMarginTop !== undefined ? `
      ${containerSelector} .cartx-necessary-input-star {
        ${r.inputMarginLeft !== undefined ? `left: calc(9px + ${r.inputMarginLeft}px) !important;` : ''}
        ${r.inputMarginTop !== undefined ? `top: calc(10px + ${r.inputMarginTop}px) !important;` : ''}
      }
      ${containerSelector} .cartx-necessary-input-starphone {
        ${r.inputMarginLeft !== undefined ? `left: calc(12px + ${r.inputMarginLeft}px) !important;` : ''}
        ${r.inputMarginTop !== undefined ? `top: calc(10px + ${r.inputMarginTop}px) !important;` : ''}
      }
      ` : ''}
      ${containerSelector} .cartx-form[data-form-type="options"] {
        ${r.optionMarginTop !== undefined ? `margin-top: ${r.optionMarginTop}px !important;` : ''}
        ${r.optionMarginBottom !== undefined ? `margin-bottom: ${r.optionMarginBottom}px !important;` : ''}
        ${r.optionMarginLeft !== undefined ? `margin-left: ${r.optionMarginLeft}px !important;` : ''}
        ${r.optionMarginRight !== undefined ? `margin-right: ${r.optionMarginRight}px !important;` : ''}
      }
      ${containerSelector} .cartx-form[data-form-type="dropdown"] {
        ${r.dropdownMarginTop !== undefined ? `margin-top: ${r.dropdownMarginTop}px !important;` : ''}
        ${r.dropdownMarginBottom !== undefined ? `margin-bottom: ${r.dropdownMarginBottom}px !important;` : ''}
        ${r.dropdownMarginLeft !== undefined ? `margin-left: ${r.dropdownMarginLeft}px !important;` : ''}
        ${r.dropdownMarginRight !== undefined ? `margin-right: ${r.dropdownMarginRight}px !important;` : ''}
      }
      ${containerSelector} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select {
        border: none !important;
        background: none !important;
        ${r.dropdownMarginLeft !== undefined || r.dropdownMarginRight !== undefined ? `width: calc(100% - ${(r.dropdownMarginLeft || 0) + (r.dropdownMarginRight || 0)}px) !important;` : ''}
      }
      /* ${pageValue} 状态的 email-suffix-list 样式（覆盖原有样式） */
      ${containerSelector} .email-suffix-list{
        border-width: ${r.inputType === 'bottom' ? '0 0 1px 0' : '1px'}!important;
        border-style: ${r.inputBorderStyle || 'solid'}!important;
        border-color: ${r.inputBorderColor || '#C5C4C3'}!important;
        ${`border-radius: ${(r.inputBorderRadius === '1000px' ? '16px' : r.inputBorderRadius) || '4px'};`}
      }
      ${containerSelector} .email-suffix-list .email-suffix-item {
        color: ${r.inputColor};
      }
      /* ${pageValue} 状态的 placeholder 和 picker 样式（覆盖原有样式） */
      ${containerSelector} .cartx-form .arco-picker input {
        color: ${r.inputColor || '#1d2129'}!important;
      }
      ${containerSelector} .cartx-form .arco-input::-webkit-input-placeholder,
      ${containerSelector} .cartx-form .arco-picker::-webkit-input-placeholder,
      ${containerSelector} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view::-webkit-input-placeholder,
      ${containerSelector} .cartx-form .arco-picker input::-webkit-input-placeholder {
        color: ${r.inputColor || '#1d2129'}!important;
        opacity: .3;
      }
      ${containerSelector} .cartx-form .arco-input:-moz-placeholder,
      ${containerSelector} .cartx-form .arco-picker:-moz-placeholder,
      ${containerSelector} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view:-moz-placeholder,
      ${containerSelector} .cartx-form .arco-picker input:-moz-placeholder {
        color: ${r.inputColor || '#1d2129'}!important;
        opacity: .3;
      }
      ${containerSelector} .cartx-form .arco-input::-moz-placeholder,
      ${containerSelector} .cartx-form .arco-picker::-moz-placeholder,
      ${containerSelector} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view::-moz-placeholder,
      ${containerSelector} .cartx-form .arco-picker input::-moz-placeholder {
        color: ${r.inputColor || '#1d2129'}!important;
        opacity: .3;
      }
      ${containerSelector} .cartx-form .arco-input:-ms-input-placeholder,
      ${containerSelector} .cartx-form .arco-picker:-ms-input-placeholder,
      ${containerSelector} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view:-ms-input-placeholder,
      ${containerSelector} .cartx-form .arco-picker input:-ms-input-placeholder {
        color: ${r.inputColor || '#1d2129'}!important;
        opacity: .3;
      }
      ${containerSelector} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-select-view {
        border-width: 1px !important;
        border-style: ${typeof r.dropdownBorderStyle !== 'undefined' ? r.dropdownBorderStyle : 'solid'} !important;
        border-color: ${typeof r.dropdownBorderColor !== 'undefined' ? r.dropdownBorderColor : '#C5C4CE'} !important;
        border-radius: ${typeof r.dropdownBorderRadius !== 'undefined' ? r.dropdownBorderRadius : '4px'} !important;
        background: ${r.dropdownBackgroundType !== 'none' ? (typeof r.dropdownBackgroundColor !== 'undefined' ? r.dropdownBackgroundColor : '#FFFFFF') : 'none'} !important;
        color: ${r.dropdownOptionTextColor || '#1d2129'} !important;
        --cartx-color-text-1: ${r.dropdownOptionTextColor || '#1d2129'} !important;
        --cartx-color-text-3: ${r.dropdownOptionTextColor || '#1d2129'} !important;
      }
      ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select.cartx-dropdown-select__${pageValue} .arco-select-view {
        color: ${r.dropdownOptionTextColor || '#1d2129'} !important;
        --cartx-color-text-1: ${r.dropdownOptionTextColor || '#1d2129'} !important;
        --cartx-color-text-3: ${r.dropdownOptionTextColor || '#1d2129'} !important;
      }
      ${containerSelector} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-select-view-value,
      ${containerSelector} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-input {
        color: ${r.dropdownOptionTextColor || '#1d2129'} !important;
        opacity: 1 !important;
      }
      ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select.cartx-dropdown-select__${pageValue} .arco-select-view-value,
      ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select.cartx-dropdown-select__${pageValue} .arco-input {
        color: ${r.dropdownOptionTextColor || '#1d2129'} !important;
        opacity: 1 !important;
      }
      ${containerSelector} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-select-view-input::-webkit-input-placeholder,
      ${containerSelector} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-select-view-input::placeholder {
        color: ${r.dropdownPlaceholderColor || '#C5C4CE'} !important;
        font-size: ${r.dropdownPlaceholderFontSize || '14px'} !important;
        opacity: .3 !important;
      }
      ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select.cartx-dropdown-select__${pageValue} .arco-select-view-input::-webkit-input-placeholder,
      ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select.cartx-dropdown-select__${pageValue} .arco-select-view-input::placeholder {
        color: ${r.dropdownPlaceholderColor || '#C5C4CE'} !important;
        font-size: ${r.dropdownPlaceholderFontSize || '14px'} !important;
        opacity: .3 !important;
      }
      .cartx-dropdown-popper__cartx-dropdown-select__${pageValue} .arco-select-option,
      .cartx-dropdown-menu__cartx-dropdown-select__${pageValue} .arco-select-option {
        color: ${r.dropdownOptionTextColor || '#1d2129'} !important;
        font-family: ${r.dropdownOptionTextFontFamily || 'Arial, sans-serif'} !important;
      }
      ${containerSelector} .cartx-dropdown-title {
        text-align: left !important;
        line-height: 22px !important;
        margin-bottom: 8px !important;
        font-weight: 400 !important;
        ${r.dropdownTitleColor ? `color: ${r.dropdownTitleColor} !important;` : ''}
        ${r.dropdownTitleFontSize ? `font-size: ${r.dropdownTitleFontSize} !important;` : ''}
        ${r.dropdownTitleFontFamily ? `font-family: ${r.dropdownTitleFontFamily} !important;` : ''}
      }

      /* ${pageValue} option 样式 */
      ${containerSelector} .cartx-option-columns:not(.cartx-b-privacy-new) {
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 8px 16px !important;
      }
      ${containerSelector} .cartx-form[data-form-type="options"] .arco-form-item-control {
        min-height: 0 !important;
      }
      ${containerSelector} .cartx-option-columns:not(.cartx-b-privacy-new) .arco-radio,
      ${containerSelector} .cartx-option-columns:not(.cartx-b-privacy-new) .arco-checkbox {
        flex: 0 0 ${getOptionFlexBasis(r.optionColumns)} !important;
        min-width: 0 !important;
        max-width: 100% !important;
        margin-right: 0 !important;
        margin-bottom: 0 !important;
      }
      ${containerSelector} .cartx-option-columns:not(.cartx-b-privacy-new) .arco-radio-text,
      ${containerSelector} .cartx-option-columns:not(.cartx-b-privacy-new) .arco-checkbox-text {
        color: ${r.optionTextColor || r.optionColor || '#1D2129'}!important;
        font-family: ${r.optionFontFamily || 'Arial, sans-serif'}!important;
        font-size: ${r.optionFontSize || '14px'}!important;
      }
      ${containerSelector} .cartx-option-columns .arco-checkbox-mask,
      ${containerSelector} .cartx-option-columns .arco-radio-mask{
        border-color: #C5C4CE !important;
      }
      ${containerSelector} .cartx-option-columns .arco-checkbox-checked .arco-checkbox-mask,
      ${containerSelector} .cartx-option-columns .arco-radio-checked .arco-radio-mask {
        border-color: ${r.optionBackground || '#1D2129'}!important;
        background: ${r.optionBackground || '#1D2129'}!important;
      }
      ${containerSelector} .cartx-question {
        color: ${r.optionTitleColor || r.optionColor || '#1D2129'}!important;
        font-family: ${r.optionFontFamily || 'Arial, sans-serif'}!important;
        font-size: ${r.optionTitleFontSize || '16px'}!important;
      }
      ${containerSelector} .cartx-question-luola {
        color: ${r.optionTitleColor || r.optionColor || '#1D2129'}!important;
        font-family: ${r.optionFontFamily || 'Arial, sans-serif'}!important;
        font-size: ${r.optionTitleFontSize || '16px'}!important;
      }

      /* ${pageValue} privacy */
      ${containerSelector} .cartx-privacy-new-wrapper {
        color: ${styleMap[privacyStyleKey]?.['color']} !important;
        background-color: ${styleMap[privacyStyleKey]?.['background-color']} !important;
        margin-top: ${styleMap[privacyStyleKey]?.['margin-top'] || 0}px !important;
        margin-bottom: ${styleMap[privacyStyleKey]?.['margin-bottom'] || 0 }px  !important;
        margin-left: ${styleMap[privacyStyleKey]?.['margin-left'] || 0}px  !important;
        margin-right: ${styleMap[privacyStyleKey]?.['margin-right'] || 0}px  !important;
        padding-top: ${styleMap[privacyStyleKey]?.['padding-top'] || 5}px !important;
        padding-bottom: ${styleMap[privacyStyleKey]?.['padding-bottom'] || 5}px  !important;
        padding-left: ${styleMap[privacyStyleKey]?.['padding-left'] || 10}px  !important;
        padding-right: ${styleMap[privacyStyleKey]?.['padding-right'] || 10}px  !important;
        align-items: ${textAlignMap[styleMap[privacyStyleKey]?.['text-align'] || 'left']};
      }
      ${containerSelector} .cartx-option-columns.cartx-b-privacy-new .arco-radio-text,
      ${containerSelector} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-text,
      ${containerSelector} .cartx-option-columns.cartx-b-privacy-new .cartx-checkbox-set-text {
        color: ${styleMap[privacyStyleKey]?.color}!important;
        font-family: ${styleMap[privacyStyleKey]?.['font-family']} !important;
      }
      ${containerSelector} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-checked .arco-checkbox-mask,
      ${containerSelector} .cartx-option-columns.cartx-b-privacy-new .arco-radio-checked .arco-radio-mask {
        border-color: ${styleMap[privacyCheckStyleKey]?.color} !important;
        background: ${styleMap[privacyCheckStyleKey]?.color} !important;
      }
    `;

    return style;
  }

  const getMiddleStyles = () => {
    if (middlePageValues.length === 0) return '';
    return middlePageValues.map(pageValue => getMiddlePageStyle(pageValue)).join('\n');
  }

  let customFontStyle = '';
  const escapeCssString = (value: string) =>
    String(value ?? '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  const companyFontList = safeJsonParse<any[]>(window.localStorage.getItem('companyFontList'), []);
  if (companyFontList?.length > 0) {
    companyFontList.forEach(({ fontName, url }) => {
      const safeFontName = escapeCssString(fontName);
      const safeUrl = escapeCssString(url);
      customFontStyle += `
        @font-face {
          font-family: "${safeFontName}";
          src: url("${safeUrl}") format('truetype'),
              url("${safeUrl}") format('opentype'),
              url("${safeUrl}") format('woff'),
              url("${safeUrl}") format('woff2');
          font-display: swap;
        }
      `;
    });
  }

  const stylesElement3 = `${id} .cartx-option-columns{
      text-align: left;
    }
    ${customFontStyle}
    ${enableGlobalFontFamily ? `${id} .cartx-a * {font-family: ${globalFontFamily};}` : ''}
    ${id} .cartx-option-columns:not(.cartx-b-privacy-new) {
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 8px 16px !important;
    }
    ${id} .cartx-form[data-form-type="options"] .arco-form-item-control {
      min-height: 0 !important;
    }
    ${id} .cartx-option-columns:not(.cartx-b-privacy-new) .arco-radio,
    ${id} .cartx-option-columns:not(.cartx-b-privacy-new) .arco-checkbox {
      flex: 0 0 ${getOptionFlexBasis(rules.optionColumns)} !important;
      min-width: 0 !important;
      max-width: 100% !important;
      margin-right: 0 !important;
      margin-bottom: 0 !important;
    }
    ${id} .cartx-option-columns:not(.cartx-b-privacy-new) .arco-radio-text,
    ${id} .cartx-option-columns:not(.cartx-b-privacy-new) .arco-checkbox-text {
      color: ${rules.optionTextColor || rules.optionColor || '#1D2129'}!important;
      font-family: ${rules.optionFontFamily || 'Arial, sans-serif'}!important;
      font-size: ${rules.optionFontSize || '14px'}!important;
    }
    ${id} .cartx-option-columns .arco-checkbox-mask,
    ${id} .cartx-option-columns .arco-radio-mask{
      border-color: #C5C4CE !important;
    }
    ${id} .cartx-option-columns .arco-checkbox-checked .arco-checkbox-mask,
    ${id} .cartx-option-columns .arco-radio-checked .arco-radio-mask {
      border-color: ${rules.optionBackground || '#1D2129'}!important;
      background: ${rules.optionBackground || '#1D2129'}!important;
    }
    ${id} .cartx-privacy-new-wrapper {
      color: ${styleMap['cartx-b-privacy']?.['color']} !important;
      background-color: ${styleMap['cartx-b-privacy']?.['background-color']} !important;
      margin-top: ${styleMap['cartx-b-privacy']?.['margin-top'] || 0}px !important;
      margin-bottom: ${styleMap['cartx-b-privacy']?.['margin-bottom'] || 0 }px  !important;
      margin-left: ${styleMap['cartx-b-privacy']?.['margin-left'] || 0}px  !important;
      margin-right: ${styleMap['cartx-b-privacy']?.['margin-right'] || 0}px  !important;
      padding-top: ${styleMap['cartx-b-privacy']?.['padding-top'] || 5}px !important;
      padding-bottom: ${styleMap['cartx-b-privacy']?.['padding-bottom'] || 5}px  !important;
      padding-left: ${styleMap['cartx-b-privacy']?.['padding-left'] || 10}px  !important;
      padding-right: ${styleMap['cartx-b-privacy']?.['padding-right'] || 10}px  !important;
      min-width: unset !important;
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      align-items: ${textAlignMap[styleMap['cartx-b-privacy']?.['text-align'] || 'left']};
    }
    ${id} .cartx-privacy-new-wrapper .cartx-privacy-new-inner {
      box-sizing: border-box;
    }
    ${id} .cartx-privacy-box.cartx-privacy-box-new {
      border-radius: 0;
      padding: 0;
      background-color: unset;
      margin: 0;
    }
    ${id} .cartx-privacy-box.cartx-privacy-box-new .arco-form-item-control {
      min-height: 26px;
    }
    ${id} .cartx-option-columns.cartx-b-privacy-new .cartx-checkbox-set .arco-checkbox-mask-wrapper {
      margin-left: 0;
    }
    ${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio-text,
    ${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-text,
    ${id} .cartx-option-columns.cartx-b-privacy-new .cartx-checkbox-set-text {
      color: ${styleMap['cartx-b-privacy']?.color}!important;
      font-family: ${styleMap['cartx-b-privacy']?.['font-family']} !important;
      padding-top: 1px;
      padding-bottom: 1px;
    }
    ${id} .cartx-a .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-mask,
    ${id} .cartx-a .cartx-option-columns.cartx-b-privacy-new .arco-radio-mask{
      border-width: 1px;
      border-color: ${styleMap['cartx-b-privacy']?.color}!important;
    }
    ${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox-checked .arco-checkbox-mask,
    ${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio-checked .arco-radio-mask {
      border-color: ${styleMap['cartx-b-privacy-check']?.color} !important;
      background: ${styleMap['cartx-b-privacy-check']?.color} !important;
    }
    ${id} .cartx-option-columns.cartx-b-privacy-new .arco-radio,
    ${id} .cartx-option-columns.cartx-b-privacy-new .arco-checkbox {
      min-width: unset !important;
      margin-right: 0 !important;
    }

    ${id} .cartx-question {
      color: ${rules.optionTitleColor || rules.optionColor || '#1D2129'}!important;
      font-family: ${rules.optionFontFamily || 'Arial, sans-serif'}!important;
      font-size: ${rules.optionTitleFontSize || '16px'}!important;
    }
    ${id} .cartx-question-luola {
      color: ${rules.optionTitleColor || rules.optionColor || '#1D2129'}!important;
      font-family: ${rules.optionFontFamily || 'Arial, sans-serif'}!important;
      font-size: ${rules.optionTitleFontSize || '16px'}!important;
    }
    ${id} .cartx-form .cartx-input .arco-input,
    ${id} .cartx-form .cartx-input,
    ${id} .cartx-form .arco-picker,
    ${id} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view{
      border-width: ${rules.inputType === 'bottom' ? '0 0 1px 0' : '1px'}!important;
      border-style: ${rules.inputBorderStyle || 'solid'}!important;
      border-color: ${rules.inputBorderColor || '#C5C4C3'}!important;
      border-radius: ${rules.inputBorderRadius || '4px'}!important;
      background: ${rules.inputBackgroundShow !== 'none' ? rules.inputBackground || '#FFFFFF' : 'none'}!important;
      color: ${rules.inputColor || '#1d2129'}!important;
      ${companyUUID === 'm7bbi3hs' ? `-webkit-text-fill-color: ${rules.inputColor || '#1d2129'} !important;` : ''}
    }
    /* Arco 默认 focus 时给 picker 内部 input 套 var(--color-primary-light-1) 背景，看起来像浅色"白框"，强制透明让外层 picker 背景生效 */
    ${id} .cartx-form .arco-picker input,
    ${id} .cartx-form .arco-picker-focused .arco-picker-input-active input,
    ${id} .cartx-form .arco-picker-focused:hover .arco-picker-input-active input {
      background: transparent !important;
    }
    ${id} .cartx-form[data-form-type="input"] {
      ${rules.inputMarginTop !== undefined ? `margin-top: ${rules.inputMarginTop}px !important;` : ''}
      ${rules.inputMarginBottom !== undefined ? `margin-bottom: ${rules.inputMarginBottom}px !important;` : ''}
      ${rules.inputMarginLeft !== undefined ? `margin-left: ${rules.inputMarginLeft}px !important;` : ''}
      ${rules.inputMarginRight !== undefined ? `margin-right: ${rules.inputMarginRight}px !important;` : ''}
      width: calc(100% - ${rules.inputMarginLeft || 0}px - ${rules.inputMarginRight || 0}px) !important;
    }
    ${rules.inputMarginLeft !== undefined || rules.inputMarginTop !== undefined ? `
    ${id} .cartx-necessary-input-star {
      ${rules.inputMarginLeft !== undefined ? `left: calc(9px + ${rules.inputMarginLeft}px) !important;` : ''}
      ${rules.inputMarginTop !== undefined ? `top: calc(10px + ${rules.inputMarginTop}px) !important;` : ''}
    }
    ${id} .cartx-necessary-input-starphone {
      ${rules.inputMarginLeft !== undefined ? `left: calc(12px + ${rules.inputMarginLeft}px) !important;` : ''}
      ${rules.inputMarginTop !== undefined ? `top: calc(10px + ${rules.inputMarginTop}px) !important;` : ''}
    }
    ` : ''}
    ${id} .cartx-form[data-form-type="options"] {
      ${rules.optionMarginTop !== undefined ? `margin-top: ${rules.optionMarginTop}px !important;` : ''}
      ${rules.optionMarginBottom !== undefined ? `margin-bottom: ${rules.optionMarginBottom}px !important;` : ''}
      ${rules.optionMarginLeft !== undefined ? `margin-left: ${rules.optionMarginLeft}px !important;` : ''}
      ${rules.optionMarginRight !== undefined ? `margin-right: ${rules.optionMarginRight}px !important;` : ''}
    }
    ${id} .cartx-form[data-form-type="dropdown"] {
      ${rules.dropdownMarginTop !== undefined ? `margin-top: ${rules.dropdownMarginTop}px !important;` : ''}
      ${rules.dropdownMarginBottom !== undefined ? `margin-bottom: ${rules.dropdownMarginBottom}px !important;` : ''}
      ${rules.dropdownMarginLeft !== undefined ? `margin-left: ${rules.dropdownMarginLeft}px !important;` : ''}
      ${rules.dropdownMarginRight !== undefined ? `margin-right: ${rules.dropdownMarginRight}px !important;` : ''}
    }
    ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select {
      border: none !important;
      background: none !important;
      ${rules.dropdownMarginLeft !== undefined || rules.dropdownMarginRight !== undefined ? `width: calc(100% - ${(rules.dropdownMarginLeft || 0) + (rules.dropdownMarginRight || 0)}px) !important;` : ''}
    }
    ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-select-view {
      border-width: 1px !important;
      border-style: ${typeof rules.dropdownBorderStyle !== 'undefined' ? rules.dropdownBorderStyle : 'solid'} !important;
      border-color: ${typeof rules.dropdownBorderColor !== 'undefined' ? rules.dropdownBorderColor : '#C5C4CE'} !important;
      border-radius: ${typeof rules.dropdownBorderRadius !== 'undefined' ? rules.dropdownBorderRadius : '4px'} !important;
      background: ${rules.dropdownBackgroundType !== 'none' ? (typeof rules.dropdownBackgroundColor !== 'undefined' ? rules.dropdownBackgroundColor : '#FFFFFF') : 'none'} !important;
      color: ${rules.dropdownOptionTextColor || '#1d2129'} !important;
      --cartx-color-text-1: ${rules.dropdownOptionTextColor || '#1d2129'} !important;
      --cartx-color-text-3: ${rules.dropdownOptionTextColor || '#1d2129'} !important;
    }
    ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-select-view-value,
    ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-input {
      color: ${rules.dropdownOptionTextColor || '#1d2129'} !important;
      opacity: 1 !important;
    }
    ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-select-view-input::-webkit-input-placeholder,
    ${id} .cartx-form[data-form-type="dropdown"] .cartx-dropdown-select .arco-select-view-input::placeholder {
      color: ${rules.dropdownPlaceholderColor || '#C5C4CE'} !important;
      font-size: ${rules.dropdownPlaceholderFontSize || '14px'} !important;
      opacity: .3 !important;
    }
    .cartx-dropdown-popper__cartx-dropdown-select__start .arco-select-option,
    .cartx-dropdown-menu__cartx-dropdown-select__start .arco-select-option {
      color: ${rules.dropdownOptionTextColor || '#1d2129'} !important;
      font-family: ${rules.dropdownOptionTextFontFamily || 'Arial, sans-serif'} !important;
    }
    ${id} .cartx-dropdown-title {
      text-align: left !important;
      line-height: 22px !important;
      margin-bottom: 8px !important;
      font-weight: 400 !important;
      ${rules.dropdownTitleColor ? `color: ${rules.dropdownTitleColor} !important;` : ''}
      ${rules.dropdownTitleFontSize ? `font-size: ${rules.dropdownTitleFontSize} !important;` : ''}
      ${rules.dropdownTitleFontFamily ? `font-family: ${rules.dropdownTitleFontFamily} !important;` : ''}
    }
    ${id} .cartx-form-wrapper {
      position: relative;
    }
    ${id} .cartx-form-wrapper .arco-form-message {
      text-align: left;
    }
    ${enableOptimizeEmailHeightStyle ? `${id} .cartx-b-show.cartx-b-show-left, ${id} .cartx-b-show.cartx-b-show-right {overflow: visible}` : ''}
    ${enableOptimizeEmailHeightStyle && false ? `${id} .cartx-b-typesetting-content {overflow-y: unset}` : ''}
    ${enableOptimizeEmailHeightStyle ? `${id} .cartx-b-show-left .cartx-b-typesetting-content-inner, ${id} .cartx-b-show-right .cartx-b-typesetting-content-inner {overflow-y: unset}` : ''}
    ${id} .cartx-a-overlay {background-color: rgba(0, 0, 0, 0.75);}
    ${id} .cartx-b-wheel-container {box-shadow: 0 0 10px #000; border-radius: 18px;}
    ${id} .cartx-b-wheel-container:has(.cartx-b-zhuanpan-bg) {border-radius: 18px;}

    ${id} .cartx-a {letter-spacing: 0;}
    ${id} .cartx-b-button, ${id} .cartx-b-button-end {font-weight: inherit;}
    ${id} .cartx-b-button, ${id} .cartx-b-button-end {font-weight: inherit;}
    ${id} .cartx-b-subtitle, ${id} .cartx-b-subtitle-end {font-weight: inherit;}
    ${id} .cartx-b-show-email {font-weight: inherit;}
    ${id} .cartx-b-show-email .arco-form-message {font-weight: 400;}

    ${
      [0, 2].includes(triggerEventType)
        ? `
          @media screen and (max-width: 500px) {
            ${id} .cartx-b-wheel-container {margin-top: 30px;}
          }
        `
        : ''
    }

    ${`
          @keyframes shake {
            0% { transform: translate3d(0, 0, 0); }
            10%, 30%, 50%, 70%, 90% { transform: translate3d(-10px, 0, 0); }
            20%, 40%, 60%, 80% { transform: translate3d(10px, 0, 0); }
            100% { transform: translate3d(0, 0, 0); }
          }
          ${id} .arco-form-item-status-error .arco-input-error,
          ${id} .arco-form-item-status-error .arco-picker-error,
          ${id} .arco-form-item-status-error .arco-select-error,
          ${id} .arco-form-item-status-error + .cartx-necessary-input-star,
          ${id} .arco-form-item-status-error + .cartx-necessary-input-starphone {
            animation: shake 1s;
            will-change: transform;
            transform-origin: top center;
            animation-fill-mode: both;
          }
        `}

    @media screen and (max-width: 800px) {
      ${
        `${id} .cartx-b-wheel-container .cartx-b-show,
        ${id} .cartx-b-wheel-container .cartx-b-show__middle,
        ${id} .cartx-b-wheel-container .cartx-b-show__middle2,
        ${id} .cartx-b-wheel-container .cartx-b-show__middle3,
        ${id} .cartx-b-wheel-container .cartx-b-show__middle4,
        ${id} .cartx-b-wheel-container .cartx-b-show__end {max-height: unset !important;}`
      }
      ${
        `${id} .cartx-b-show,
        ${id} .cartx-b-show__middle,
        ${id} .cartx-b-show__middle2,
        ${id} .cartx-b-show__middle3,
        ${id} .cartx-b-show__middle4,
        ${id} .cartx-b-show__end {height: unset !important;}`
      }
      ${`${id} .cartx-b-typesetting-content {max-height: unset;}`}

      ${id} .cartx-b-show .cartx-b-typesetting-content-inner {padding: 10px !important;}
      ${id} .cartx-b-zhuanpan-bg .cartx-b-typesetting-content-inner {padding: 24px 16px !important;}
      ${id} .cartx-b-show-left .cartx-b-title, ${id} .cartx-b-show-right .cartx-b-title {margin-bottom: 10px;}
      ${id} .cartx-b-show-left .cartx-b-activity, ${id} .cartx-b-show-right .cartx-b-activity {margin-bottom: 10px;}
      ${id} .cartx-b-show-left .cartx-b-show-email, ${id} .cartx-b-show-right .cartx-b-show-email {margin-top: 10px;}
      ${`
            ${id} .cartx-b-show .cartx-ad-image-box {display: flex; margin-top: 10px;}
            ${id} .cartx-b-show .cartx-ad-image {
              width: 50%;
              background-color: #f2f2f2;
              display: flex;
              align-items: center;
              flex-direction: column;
              height: auto;
            }
            ${id} .cartx-b-show .cartx-ad-image img {height: auto;}
        `}

      ${!isFullscreenPopup ? `${id} .cartx-a-overlay {pointer-events: none;}` : ''}
      ${!isFullscreenPopup ? `${id} .cartx-a {pointer-events: auto;}` : ''}

      ${enableOptimizeEmailHeightStyle ? `${id} .cartx-b-wheel-container {overflow: visible;}` : ''}
      ${enableOptimizeEmailHeightStyle ? `${id} .cartx-b-typesetting-content {overflow: visible;}` : ''}
      ${enableOptimizeEmailHeightStyle ? `${id} .cartx-b-typesetting-content-inner {overflow: visible;}` : ''}
    }

    ${enableOptimizeEmailHeightStyle ? `${id} .cartx-b-show {overflow: visible}` : ''}
    ${enableOptimizeEmailHeightStyle ? `${id} .cartx-b-show.cartx-b-zhuanpan-bg, ${id} .cartx-b-show.cartx-combination-box {overflow-y: visible; overflow-x: hidden;}` : ''}
    ${id} .email-suffix-list{
      width: 100%;
      ${enableOptimizeEmailHeightStyle ? (terminal && !['top', 'middle'].includes(imgPosition) ? 'max-height: 110px;' : 'max-height: 800px;') : 'max-height: 110px;'}
      ${enableOptimizeEmailHeightStyle ? (terminal && !['top', 'middle'].includes(imgPosition) ? 'overflow-y: scroll;' : '') : 'overflow-y: scroll;'}
      text-indent: 16px;
      text-align: left;
      line-height: 30px;
      position: absolute;
      ${enableOptimizeEmailHeightStyle ? (terminal && !['top', 'middle'].includes(imgPosition) ? 'top: 69%;' : 'bottom: 95%;') : 'bottom: 95%;'}
      left: 0;
      z-index: 100;
      background: #fff;
      font-size: 12px;
      border-width: ${rules.inputType === 'bottom' ? '0 0 1px 0' : '1px'}!important;
      border-style: ${rules.inputBorderStyle || 'solid'}!important;
      border-color: ${rules.inputBorderColor || '#C5C4C3'}!important;
      ${`border-radius: ${(rules.inputBorderRadius === '1000px' ? '16px' : rules.inputBorderRadius) || '4px'};`}
      ${enableOptimizeEmailHeightStyle && !(terminal && !['top', 'middle'].includes(imgPosition)) ? 'overflow: hidden;' : ''}
    }
    ${id} .email-suffix-list .email-suffix-item {
      cursor: pointer;
      line-height: 30px;
      font-family: Arial, sans-serif;
      color: ${rules.inputColor || '#1d2129'};
    }
    ${id} .cartx-b-close {
    }
    ${`
          ${id} .email-suffix-list .email-suffix-item:after {
            content: '';
            height: 0.5px;
            line-height: 0.5px;
            display: block;
            margin: 0 4px;
            background: ${hexToRgb(rules.inputBorderColor || '#0D0C16', 0.3)};
          }
          ${
            middlePageValues.length > 0
              ? middlePageValues.map(pageValue => {
                  const scopedBorderColor = getScopedRule(pageValue, 'middleInputBorderColor');
                  return `${id} .cartx-b-wheel-container__${pageValue} .email-suffix-list .email-suffix-item:after {
                    background: ${hexToRgb(scopedBorderColor || rules.inputBorderColor || '#0D0C16', 0.3)};
                  }`;
                }).join('\n')
              : ''
          }
        `}
    ${id} .email-suffix-list .email-suffix-item:last-child {
      border-bottom: none
    }
    ${id} .email-suffix-list .email-suffix-item:hover{
      background: rgb(242 242 243);
    }
    ${id} .cartx-form .arco-picker input {
      color: ${rules.inputColor || '#1d2129'}!important;
    }
    ${id} .cartx-form .cartx-luola-select-color .arco-select-view .arco-select-view-selector .arco-select-view-input::placeholder {
      color: ${rules.optionTextColor || rules.optionColor || '#1d2129'}!important;
      font-size: 12px!important;
      opacity: .3;
    }
    ${id} .cartx-form .cartx-luola-select-color .arco-select-view .arco-select-view-selector{
      color: ${rules.optionTextColor || rules.optionColor || '#1d2129'}!important;
    }
    ${id} .cartx-form .arco-input::-webkit-input-placeholder,
    ${id} .cartx-form .arco-picker::-webkit-input-placeholder,
    ${id} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view::-webkit-input-placeholder,
    ${id} .cartx-form .arco-picker input::-webkit-input-placeholder {
      color: ${rules.inputColor}!important;
      opacity: .3;
    }
    ${id} .cartx-form .arco-input:-moz-placeholder,
    ${id} .cartx-form .arco-picker:-moz-placeholder,
    ${id} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view:-moz-placeholder,
    ${id} .cartx-form .arco-picker input:-moz-placeholder {
      color: ${rules.inputColor}!important;
      opacity: .3;
    }
    ${id} .cartx-form .arco-input::-moz-placeholder,
    ${id} .cartx-form .arco-picker::-moz-placeholder,
    ${id} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view::-moz-placeholder,
    ${id} .cartx-form .arco-picker input::-moz-placeholder {
      color: ${rules.inputColor}!important;
      opacity: .3;
    }
    ${id} .cartx-form .arco-input:-ms-input-placeholder,
    ${id} .cartx-form .arco-picker:-ms-input-placeholder,
    ${id} .cartx-form .arco-select:not(.cartx-dropdown-select) .arco-select-view:-ms-input-placeholder,
    ${id} .cartx-form .arco-picker input:-ms-input-placeholder {
      color: ${rules.inputColor}!important;
      opacity: .3;
    }

    .cartx-phone-menu .arco-select-popup-inner {
      max-height: 216px !important;
    }
    .cartx-phone-menu.cartx-phone-menu-short .arco-select-popup-inner {
      max-height: 163px !important;
    }
    .arco-select-size-large.arco-select-single .arco-select-view {
      padding: 0 12px;
    }

    ${
      enable2247StyleFix
        ? `
        .arco-select-single .arco-select-view-value,
        #cartx-wrap .cartx-form .cartx-option-columns .cartx-checkbox-set .arco-checkbox-mask-wrapper {
          margin-top: 0;
        }
        .cartx-a .cartx-b-wheel-container .cartx-b-show .cartx-b-typesetting-image-logo img {
          height: 100% !important;
        }
      `
        : ''
    }

    ${getMiddleStyles()}
  `;
  const nod3 = document.createElement('style');
  nod3.id = `${id.slice(1)}-3`;
  nod3.innerHTML = sanitizeStyleContent(stylesElement3);
  document.body.append(nod3);
};

// 合成版
/**
 * @desc 函数防抖
 * @param func 目标函数
 * @param wait 延迟执行毫秒数
 * @param immediate true - 立即执行， false - 延迟执行
 */

export const debounce = function (func, wait, immediate = undefined) {
  var timer;
  return function () {
    var context = this; // 注意 this 指向
    var args = arguments; // arguments中存着event

    if (timer) clearTimeout(timer);
    if (immediate) {
      var callNow = !timer;
      timer = setTimeout(function () {
        // 设置为null即下次callNow为true，即可实现下次执行。
        timer = null;
      }, wait);
      if (callNow) func.apply(context, args);
    } else {
      timer = setTimeout(function () {
        func.apply(context, args);
      }, wait);
    }
  };
};

// 判断当前页面是否有打开的弹框（用于避免重复弹窗）
export const noOpenModal = () => {
  const hasShowPop = getSession(STORAGE_KEY.POP_SHOW);
  if (hasShowPop) {
    return false;
  }
  const overlays: any = document.querySelectorAll('.cartx-a-overlay');
  let i = 0;
  overlays.forEach((overlay) => {
    if (overlay.style.display == 'none') {
      i += 1;
    }
  });
  if (i == overlays.length) {
    return true;
  } else {
    return false;
  }
};

// 通过 state 判断当前页面是否有打开的弹框
export const noOpenModalByState = (formidState) => {
  for (const formid in formidState) {
    if (formidState[formid]) {
      return false;
    }
  }
  return true;
};

//删除动态插入的弹框
export const deleteDOM = (id) => {
  let DOM = document.getElementById(id);
  let styleNode1 = document.getElementById(id + '-1');
  let styleNode2 = document.getElementById(id + '-2');
  let styleNode3 = document.getElementById(id + '-3');
  if (DOM) {
    document.body.removeChild(DOM);
  }
  if (styleNode1) {
    document.body.removeChild(styleNode1);
  }
  if (styleNode2) {
    document.body.removeChild(styleNode2);
  }
  if (styleNode3) {
    document.body.removeChild(styleNode3);
  }
};

/** 轮询cookie */
export const pollCookie = (callback) => {
  if (getShopifyUserId()) {
    callback();
    formEventV2({
      event: 'popup_prerequest',
      loadTime: '0'
    })
      .then((response) => {
        // console.log('数据上报成功')
      })
      .catch((e) => {});
  } else {
    let welcomeCookieQueryTimes = 0;
    let timer = setInterval(() => {
      welcomeCookieQueryTimes++;
      if (getShopifyUserId()) {
        callback();
        clearInterval(timer);
        formEventV2({
          event: 'popup_prerequest',
          loadTime: `${welcomeCookieQueryTimes * 1000}`
        })
          .then((response) => {})
          .catch((e) => {});
      } else if (welcomeCookieQueryTimes >= 30) {
        callback();
        formEventV2({
          event: 'popup_prerequest',
          loadTime: `30000`
        })
          .then((response) => {})
          .catch((e) => {});
        clearInterval(timer);
      }
    }, 1000);
  }
};
